import { useState } from "react";
import { GripHorizontal, KanbanSquare } from "lucide-react";
import { ItTicketCard } from "./ItTicketCard";
import type { ItEstado, ItGroup, ItIncident } from "../types/it";

type Props = {
  groups: ItGroup[];
  selectedIncidentId: number | null;
  canReorder: boolean;
  onOpenIncident: (incident: ItIncident) => void;
  onReorder: (ids: number[]) => Promise<void>;
};

const stateTone: Partial<
  Record<
    ItEstado,
    {
      border: string;
      softBorder: string;
      header: string;
      icon: string;
      text: string;
      orb: string;
      line: string;
      selected: string;
      selectedFooter: string;
    }
  >
> = {
  solicitada: {
    border: "border-[#dfe8e4]",
    softBorder: "border-[#dfe8e4]",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
    icon: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00d09c]/12",
    line: "from-[#0b4a2f] to-[#00d09c]",
    selected: "border-[#00d09c]/35 ring-4 ring-[#00d09c]/10",
    selectedFooter: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
  },
  aprobada: {
    border: "border-[#dfe8e4]",
    softBorder: "border-[#dfe8e4]",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
    icon: "border-[#00d09c]/18 bg-white text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00d09c]/10",
    line: "from-[#0b4a2f] to-[#00a879]",
    selected: "border-[#00d09c]/35 ring-4 ring-[#00d09c]/10",
    selectedFooter: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
  },
  creada: {
    border: "border-slate-200",
    softBorder: "border-slate-200",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)]",
    icon: "border-slate-200 bg-slate-50 text-slate-700",
    text: "text-slate-700",
    orb: "bg-slate-300/18",
    line: "from-slate-800 to-[#0b4a2f]",
    selected: "border-[#00d09c]/35 ring-4 ring-[#00d09c]/10",
    selectedFooter: "border-slate-200 bg-slate-50 text-slate-700",
  },
  iniciada: {
    border: "border-[#dfe8e4]",
    softBorder: "border-[#dfe8e4]",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f7fbf8_100%)]",
    icon: "border-[#00d09c]/16 bg-[#00d09c]/7 text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00d09c]/10",
    line: "from-slate-800 to-[#00a879]",
    selected: "border-[#00d09c]/35 ring-4 ring-[#00d09c]/10",
    selectedFooter: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
  },
  corregida: {
    border: "border-[#dfe8e4]",
    softBorder: "border-[#dfe8e4]",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
    icon: "border-[#00c3af]/18 bg-[#00c3af]/8 text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00c3af]/10",
    line: "from-[#0b4a2f] to-[#00c3af]",
    selected: "border-[#00c3af]/35 ring-4 ring-[#00c3af]/10",
    selectedFooter: "border-[#00c3af]/18 bg-[#00c3af]/8 text-[#0b4a2f]",
  },
  rechazada: {
    border: "border-rose-200",
    softBorder: "border-rose-100",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#fff1f2_100%)]",
    icon: "border-rose-100 bg-rose-50 text-rose-700",
    text: "text-rose-700",
    orb: "bg-rose-300/18",
    line: "from-rose-500 to-amber-400",
    selected: "border-rose-300 ring-4 ring-rose-100",
    selectedFooter: "border-rose-100 bg-rose-50/80 text-rose-700",
  },
  cerrada: {
    border: "border-slate-300",
    softBorder: "border-slate-200",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)]",
    icon: "border-slate-200 bg-slate-50 text-slate-700",
    text: "text-slate-700",
    orb: "bg-slate-300/20",
    line: "from-slate-800 to-slate-500",
    selected: "border-slate-400 ring-4 ring-slate-100",
    selectedFooter: "border-slate-200 bg-slate-50 text-slate-700",
  },
  incidencia_rechazada: {
    border: "border-red-200",
    softBorder: "border-red-100",
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#fef2f2_100%)]",
    icon: "border-red-100 bg-red-50 text-red-700",
    text: "text-red-700",
    orb: "bg-red-300/16",
    line: "from-red-500 to-rose-400",
    selected: "border-red-300 ring-4 ring-red-100",
    selectedFooter: "border-red-100 bg-red-50/80 text-red-700",
  },
};

const fallbackTone = {
  border: "border-[#dfe8e4]",
  softBorder: "border-[#dfe8e4]",
  header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
  icon: "border-[#dfe8e4] bg-white text-[#0b4a2f]",
  text: "text-[#0b4a2f]",
  orb: "bg-[#00d09c]/10",
  line: "from-[#0b4a2f] to-[#00d09c]",
  selected: "border-[#00d09c]/35 ring-4 ring-[#00d09c]/10",
  selectedFooter: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
};

function getGroupTone(groupKey: string) {
  return stateTone[groupKey as ItEstado] ?? fallbackTone;
}

export function ItTablaBoard({
  groups,
  selectedIncidentId,
  canReorder,
  onOpenIncident,
  onReorder,
}: Props) {
  const [dragState, setDragState] = useState<{
    incidentId: number;
    groupKey: string;
  } | null>(null);

  const [dropTarget, setDropTarget] = useState<{
    groupKey: string;
    incidentId: number;
    position: "before" | "after";
  } | null>(null);

  const moveWithinGroup = async (
    group: ItGroup,
    targetId: number | null,
    position: "before" | "after" | "end",
  ) => {
    if (!dragState || dragState.groupKey !== group.key) return;

    const ids = group.items.map((item) => item.id);
    const fromIndex = ids.indexOf(dragState.incidentId);
    if (fromIndex === -1) return;

    const next = [...ids];
    next.splice(fromIndex, 1);

    if (position === "end" || targetId === null) {
      next.push(dragState.incidentId);
    } else {
      const targetIndex = next.indexOf(targetId);
      if (targetIndex === -1) return;

      const insertAt = position === "before" ? targetIndex : targetIndex + 1;
      next.splice(insertAt, 0, dragState.incidentId);
    }

    if (next.join(",") === ids.join(",")) return;

    setDropTarget(null);
    setDragState(null);
    await onReorder(next);
  };

  return (
    <div className="it-board-grid">
      {groups.map((group, index) => {
        const tone = getGroupTone(group.key);
        const isDraggingOver = dragState?.groupKey === group.key;
        const hasSelected =
          selectedIncidentId !== null &&
          group.items.some((incident) => incident.id === selectedIncidentId);

        return (
          <section
            key={group.key}
            onDragOver={(event) => {
              if (!canReorder) return;
              event.preventDefault();
            }}
            onDrop={(event) => {
              if (!canReorder) return;
              event.preventDefault();
              if (event.target !== event.currentTarget) return;
              setDropTarget(null);
              void moveWithinGroup(group, null, "end");
            }}
            className={[
              "dashboard-scale-in group flex min-h-[680px] flex-col overflow-hidden rounded-[34px] border bg-white/94 shadow-[0_24px_70px_rgba(15,23,42,.09)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_34px_90px_rgba(15,23,42,.13)]",
              isDraggingOver ? tone.selected : tone.border,
              hasSelected ? tone.selected : "",
            ].join(" ")}
            style={{ animationDelay: `${index * 70}ms` }}
          >
            <header
              className={[
                "relative overflow-hidden border-b px-5 py-4",
                tone.softBorder,
                tone.header,
              ].join(" ")}
            >
              <div
                className={[
                  "pointer-events-none absolute -right-16 top-[-70px] h-40 w-40 rounded-full blur-3xl transition duration-500 group-hover:scale-125",
                  tone.orb,
                ].join(" ")}
              />

              <span
                className={[
                  "absolute inset-x-5 top-0 h-1 rounded-b-full bg-gradient-to-r",
                  tone.line,
                ].join(" ")}
              />

              <div className="relative flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "grid h-10 w-10 shrink-0 place-items-center rounded-2xl border shadow-[0_10px_24px_rgba(15,23,42,.07)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105",
                        tone.icon,
                      ].join(" ")}
                    >
                      <KanbanSquare size={17} />
                    </span>

                    <div className="min-w-0">
                      <div
                        className={[
                          "truncate text-sm font-black",
                          tone.text,
                        ].join(" ")}
                      >
                        {group.label}
                      </div>

                      <div className="mt-0.5 text-xs font-semibold text-slate-500">
                        {group.items.length} incidencia(s)
                      </div>
                    </div>
                  </div>
                </div>

                <span
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-black shadow-[0_8px_20px_rgba(15,23,42,.045)] transition-all duration-300",
                    canReorder
                      ? `${tone.softBorder} bg-white ${tone.text}`
                      : "border-slate-200 bg-white text-slate-400",
                  ].join(" ")}
                >
                  <GripHorizontal size={13} />
                  {canReorder ? "Drag" : "Fijo"}
                </span>
              </div>
            </header>

            <div className="it-column-scroll flex-1 space-y-3 bg-[radial-gradient(circle_at_top_left,rgba(0,208,156,.035),transparent_28%),#f8fafc] p-4">
              {group.items.length === 0 && (
                <div className="rounded-[26px] border border-dashed border-[#dfe8e4] bg-white/85 p-6 text-center text-sm font-bold leading-6 text-slate-400 shadow-[0_10px_26px_rgba(15,23,42,.04)]">
                  No hay incidencias en esta columna.
                </div>
              )}

              {group.items.map((incident) => (
                <ItTicketCard
                  key={incident.id}
                  incident={incident}
                  draggable={canReorder}
                  dragging={dragState?.incidentId === incident.id}
                  onDragStart={() => {
                    if (!canReorder) return;

                    setDragState({
                      incidentId: incident.id,
                      groupKey: group.key,
                    });
                  }}
                  onDragEnd={() => {
                    setDragState(null);
                    setDropTarget(null);
                  }}
                  onDragOver={(event) => {
                    if (!canReorder) return;
                    event.preventDefault();

                    const rect = event.currentTarget.getBoundingClientRect();
                    const position =
                      event.clientY < rect.top + rect.height / 2
                        ? "before"
                        : "after";

                    setDropTarget({
                      groupKey: group.key,
                      incidentId: incident.id,
                      position,
                    });
                  }}
                  onDrop={(event) => {
                    if (!canReorder) return;
                    event.preventDefault();
                    event.stopPropagation();

                    const rect = event.currentTarget.getBoundingClientRect();
                    const position =
                      event.clientY < rect.top + rect.height / 2
                        ? "before"
                        : "after";

                    void moveWithinGroup(group, incident.id, position);
                  }}
                  dropPosition={
                    dropTarget?.groupKey === group.key &&
                    dropTarget.incidentId === incident.id
                      ? dropTarget.position
                      : null
                  }
                  onClick={() => onOpenIncident(incident)}
                />
              ))}
            </div>

            {hasSelected && (
              <div
                className={[
                  "border-t px-5 py-3 text-xs font-black uppercase tracking-[0.16em]",
                  tone.selectedFooter,
                ].join(" ")}
              >
                Incidencia abierta desde esta columna
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}